import { kebabCase } from 'lodash-es';
import { ThemeVariablesEnum } from '@/enums';
import { axiosRetry } from '@/plugins/axios';

const isBrowser =
    typeof window !== 'undefined' && typeof document !== 'undefined';

/**
 * Fetches theme preview data by ID
 * @param {string} themePreviewId - The ID of the theme preview to fetch
 * @returns {Promise<Object>} - The API response
 */
export function getThemePreview(themePreviewId) {
    return axiosRetry.get(`/theme-preview/${themePreviewId}`);
}

/**
 * Loads and applies a custom font
 * @param {string} family - Font family name
 * @param {string} source - URL to font file
 * @param {string} variable - CSS variable name
 * @returns {Promise<boolean>} - Success status
 */
export async function mountCustomFont(family, variants, variable) {
    if (!isBrowser) return false;

    try {
        await Promise.all(
            variants.map(
                async ({ url, weight, style = 'normal', display = 'swap' }) => {
                    const face = new FontFace(family, `url(${url})`, {
                        weight: weight.toString(),
                        style,
                        display
                    });

                    await face.load();

                    document.fonts.add(face);
                }
            )
        );

        document.documentElement.style.setProperty(variable, `'${family}'`);
        return true;
    } catch (e) {
        console.error('Font load failed:', e);
        return false;
    }
}

/**
 * Sets a custom CSS variable
 * @param {string} key - Variable key
 * @param {string} value - Color or style value
 * @param {boolean} isStoreDesigner - Whether in store designer mode
 */
export function setCustomVariable(key, value, isStoreDesigner = false) {
    if (!isBrowser || !key || (!isStoreDesigner && !value)) {
        return;
    }

    const root = document.documentElement;
    root.style.setProperty(`--${kebabCase(key)}`, value);
}

/**
 * Applies theming (branding colors and fonts) to the application
 * @param {Object} branding - Object containing branding styles
 * @param {Object} fonts - Object containing font settings
 * @returns {Promise<void>}
 */
export async function applyTheme(branding, fonts) {
    if (!isBrowser) return;

    if (fonts && Object.keys(fonts).length) {
        for (const [key] of Object.entries(fonts)) {
            await mountCustomFont(
                fonts[key].name,
                fonts[key].variants,
                `--${key}-font`
            );
        }
    }

    // Apply branding styles
    if (branding) {
        let styles = branding;

        for (const style in branding) {
            if (style === ThemeVariablesEnum.BORDER_RADIUS_BUTTON) {
                const value = styles[style];

                if (!value) {
                    continue;
                }

                const borderRadiusOptions = {
                    none: '0px',
                    sm: '2px',
                    lg: '8px',
                    full: '9999px'
                };

                setCustomVariable(
                    style,
                    borderRadiusOptions[value] || borderRadiusOptions.sm
                );

                continue;
            }

            if (Object.values(ThemeVariablesEnum).includes(style)) {
                if (
                    style === ThemeVariablesEnum.BUTTON_FONT &&
                    styles[style] &&
                    fonts
                ) {
                    const val = fonts[styles[style]]?.name;
                    setCustomVariable(style, val, false);
                } else {
                    setCustomVariable(style, styles[style], false);
                }
            }
        }
    }
}
